import React, { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { dashboardApi } from '../services/api';

// GeoJSON for India states. We use the public path.
const INDIA_GEO_JSON = '/india-states.json';

function StateMapWidget({ title, statusFilter, data }) {
  const [stateData, setStateData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (data) {
      processIncomingData(data);
    } else {
      fetchStateDataFromAPI();
    }
  }, [statusFilter, data]);

const processIncomingData = (rawList) => {
  const total = rawList.reduce((sum, item) => sum + (item.count || 0), 0);
  
  const processed = rawList.map(item => {
    // API se aaya percentage use karo, fallback calculation se
    const percentage = item.percentage ?? (total > 0 ? (item.count / total) * 100 : 0);
    
    return {
      ...item,
      percentage: parseFloat(percentage.toFixed(1)),
      // API se aaya performance use karo, fallback calculate karo
      performance: item.performance || (
        percentage >= 15 ? 'HIGH' : percentage >= 7 ? 'MID' : 'LOW'
      )
    };
  }).sort((a, b) => b.count - a.count);

  setStateData(processed);
  setTotalCount(total);
  setLoading(false);
};

  const fetchStateDataFromAPI = async () => {
    try {
      setLoading(true);
      const status = statusFilter || (title.toLowerCase().includes('delivered') ? 'DELIVERED' : 'CONFIRMED');
      const response = await dashboardApi.getOrdersByState({ status });

      let rawData = [];
      if (response && response.stateData && Array.isArray(response.stateData)) {
        rawData = response.stateData;
      } else if (Array.isArray(response)) {
        rawData = response;
      }

      processIncomingData(rawData);
    } catch (error) {
      console.error('Error fetching state data:', error);
      setStateData([]);
      setTotalCount(0);
      setLoading(false);
    }
  };

  // Helper to map GeoJSON state names to our data state names
  const mapGeoStateName = (geoName) => {
    if (geoName === 'Arunachal Pradesh' || geoName === 'Assam' || geoName === 'Manipur' || geoName === 'Meghalaya' || geoName === 'Mizoram' || geoName === 'Nagaland' || geoName === 'Tripura') {
      return 'Assam Bengal'; // Unified for North East mapping if needed
    }
    if (geoName === 'West Bengal') return 'West Bengal';
    if (geoName === 'Orissa') return 'Odisha';
    if (geoName === 'Uttaranchal') return 'Uttarakhand';
    return geoName;
  };

  const getPerformanceColor = (performance) => {
    switch (performance?.toUpperCase()) {
      case 'HIGH': return '#2ACAB0'; // The exact minty green from the image
      case 'MID': return '#F5A66A';  // The exact soft orange from the image
      case 'LOW': return '#F88A92';  // The exact pinkish-red from the image
      default: return '#F1F5F9';
    }
  };

  const getStateColor = (geoStateName) => {
    const mappedName = mapGeoStateName(geoStateName);
    const data = stateData.find(s => s.state.toLowerCase() === mappedName.toLowerCase());

    // In case "Assam Bengal" needs to cover Assam or Bengal if not found individually
    if (!data && mappedName === 'Assam Bengal') {
      const fallback = stateData.find(s => s.state === 'Assam' || s.state === 'Assam Bengal');
      if (fallback) return getPerformanceColor(fallback.performance);
    }

    if (!data || data.count === 0) return getPerformanceColor('LOW');
    return getPerformanceColor(data.performance);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 h-full flex items-center justify-center min-h-[550px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 h-full flex flex-col font-sans">
      {/* Header with Title */}
      <h3 className="text-[17px] font-semibold text-gray-700 mb-6">{title}</h3>

      {/* Total and Legend Section Exactly like image */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-baseline space-x-2">
          <span className="text-gray-500 text-sm font-medium">Total:</span>
          <span className="text-[32px] font-black text-gray-900 leading-none">
            {totalCount.toLocaleString()}
          </span>
        </div>

        {/* Legend */}
        <div className="flex items-center space-x-6 text-xs font-semibold">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-[2px] bg-[#2ACAB0]"></div>
            <span className="text-gray-500">High</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-[2px] bg-[#F5A66A]"></div>
            <span className="text-gray-500">Mid</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-[2px] bg-[#F88A92]"></div>
            <span className="text-gray-500">Low</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col sm:flex-row gap-8 items-center py-2 h-full overflow-hidden">
        {/* Map Visualization (Even Wider) */}
        <div className="flex-[2.8] w-full relative flex items-center justify-center">
          {/* Circular background shadow like image - subtle */}
          <div className="absolute w-[95%] aspect-square rounded-full bg-teal-50 opacity-30 -z-10"></div>

          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              scale: 1600,
              center: [83, 22]
            }}
            width={850}
            height={950}
            className="w-full h-auto max-h-[1100px]"
          >
            <Geographies geography={INDIA_GEO_JSON}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={getStateColor(geo.properties.NAME_1)}
                    stroke="#1F2937"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none", fillOpacity: 0.8, cursor: 'pointer' },
                      pressed: { outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>
          </ComposableMap>
        </div>

        {/* State Statistics List */}
        <div className="flex-[1.2] min-w-[240px] w-full flex flex-col h-full bg-white self-center overflow-hidden">
          <div className="flex-1 overflow-y-auto custom-scrollbar max-h-[1050px] w-full pr-2">
            <table className="w-full">
              <tbody>
                {stateData.length > 0 ? (
                  stateData.map((item, index) => (
                    <tr key={index} className="group transition-colors">
                      <td className="py-2.5 pr-4 border-b border-transparent">
                        <span className="text-[15px] text-gray-700 font-medium whitespace-nowrap block">{item.state}</span>
                      </td>
                      <td className="py-2.5 text-right px-4 border-b border-transparent">
                        <span className="text-[15px] text-gray-900 font-bold">{item.count.toLocaleString()}</span>
                      </td>
                      <td className="py-2.5 text-right border-b border-transparent">
                        <span className={`text-[15px] font-bold ${
                          item.count === 0 ? 'text-[#F88A92]' : 
                          item.performance === 'HIGH' ? 'text-[#2ACAB0]' :
                          item.performance === 'MID' ? 'text-[#F5A66A]' : 'text-[#F88A92]'
                        }`}>
                          {item.percentage.toFixed(item.count === 0 ? 2 : 1)}%
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="3" className="text-center py-20 text-gray-400">No records found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StateMapWidget;